/* ==========================================================================
   ONNX RUNTIME EDGE INFERENCE ENGINE (/database/ml-engine.js)
   ========================================================================== */

import { checkCompatibility, getEffectiveSpecs } from './compatibility-engine.js?v=20260819-audit2';
import { state, getCaseModeSelection, getCaseStateKey, getSelectedComponentIdentity, getExactSelectedComponentSpecs } from './state.js?v=20260819-audit2';
import { debugRuntimeIssue, reportRuntimeIssue, stableKey, runtimeNow, debugPerformanceMetric } from './runtime.js?v=20260819-audit2';
import { slugifyUrlSegment } from './utils.js?v=20260819-audit2';
import { fetchCaseCommunityBuilds } from './api.js?v=20260819-phase35';
import { getRelationalOrientationClass, orientationMatrixForClass, getRelationalLayoutSpatialPrior } from './js/relational-spatial-contract.js?v=20260819-audit2';
import { normalizePsuFormFactor, resolvePsuFormFactorFromCatalog } from './js/hardware-index.js?v=20260819-audit2';

let ortSession = null;
let modelMetadata = null;
let modelMetadataLoadPromise = null;
let modelMetadataLastAttempt = 0;
let classifierInitPromise = null;
let onnxRuntimeLoadPromise = null;
let inferenceMutex = Promise.resolve();
const classifierPredictionCache = new Map();
const CLASSIFIER_CACHE_LIMIT = 512;

/* --------------------------------------------------------------------------
   DOCUMENTED / CONFIRMED BUILD SIMILARITY

   This is intentionally independent from ONNX inference. Published community
   builds provide positive real-world evidence only; absence of a match is not
   treated as evidence of incompatibility.
   -------------------------------------------------------------------------- */
const buildEvidenceCache = new Map();
const BUILD_EVIDENCE_CACHE_LIMIT = 128;

function normalizeBuildIdentity(value) {
  return String(value || '')
    .normalize('NFKC')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildPartMatches(identityName, buildPart, catalogId = '') {
  if (!buildPart || buildPart.is_custom) return false;
  const currentId = String(catalogId || '').trim().toLowerCase();
  const buildId = String(buildPart.catalog_id || buildPart.catalogId || buildPart.id || '').trim().toLowerCase();
  // Universal IDs are authoritative whenever both sides have them. A disagreement must
  // never fall back to fuzzy/name equality and accidentally turn a sibling SKU into evidence.
  if (currentId && buildId) return currentId === buildId;

  const current = normalizeBuildIdentity(identityName);
  if (!current) return false;
  const buildName = normalizeBuildIdentity(buildPart.name);
  if (buildName && current === buildName) return true;

  // Slug matching remains only as a compatibility fallback for legacy published builds
  // that pre-date catalog IDs.
  const buildSlug = normalizeBuildIdentity(String(buildPart.slug || '').replace(/[-_]+/g, ' '));
  return !!buildSlug && current === buildSlug;
}

function normalizeEvidenceMoboType(value) {
  const raw = String(value || '').trim().toUpperCase().replace(/_/g, '-');
  if (!raw) return '';
  if (raw === 'M-ATX' || raw === 'MICRO-ATX' || raw === 'MICRO ATX' || raw === 'MATX') return 'MATX';
  if (raw === 'MINI-ITX' || raw === 'MINI ITX' || raw === 'ITX') return 'ITX';
  if (raw === 'MINI-DTX' || raw === 'MINI DTX' || raw === 'DTX') return 'DTX';
  if (raw === 'ATX') return 'ATX';
  return '';
}

function normalizeEvidencePsuType(value) {
  return normalizePsuFormFactor(value);
}

function resolveBuildPsuFormFactor(buildPart) {
  return resolvePsuFormFactorFromCatalog(buildPart, state.hardwareGlobal);
}


function selectedEvidenceParts(formInputs) {
  const cooling = String(formInputs?.cooling || '').toLowerCase();
  const gpuIdentity = getSelectedComponentIdentity('gpu');
  const psuIdentity = getSelectedComponentIdentity('psu');
  const parts = [
    // GPU/cooler are exact-model evidence. Motherboard remains form-factor evidence.
    // PSU becomes exact-model evidence only when the user chose a catalog PSU; the
    // existing form-factor toggle remains the fallback when no exact model is selected.
    { key: 'gpu', match: 'exact', label: 'GPU', weight: 4, identity: gpuIdentity?.name || '', catalogId: gpuIdentity?.catalogId || '' },
    { key: 'mobo', match: 'mobo_type', label: 'Motherboard type', weight: 2, identity: normalizeEvidenceMoboType(formInputs?.mobo) },
    psuIdentity?.name
      ? { key: 'psu', match: 'exact', label: 'PSU', weight: 2, identity: psuIdentity.name, catalogId: psuIdentity.catalogId || '' }
      : { key: 'psu', match: 'psu_type', label: 'PSU type', weight: 2, identity: normalizeEvidencePsuType(formInputs?.psu) },
  ];

  if (cooling === 'liquid') {
    const aioIdentity = getSelectedComponentIdentity('aio');
    parts.push({ key: 'aio', match: 'exact', label: 'AIO', weight: 3, identity: aioIdentity?.name || '', catalogId: aioIdentity?.catalogId || '' });
  } else {
    const coolerIdentity = getSelectedComponentIdentity('cpu');
    parts.push({ key: 'cooler', match: 'exact', label: 'CPU cooler', weight: 3, identity: coolerIdentity?.name || '', catalogId: coolerIdentity?.catalogId || '' });
  }
  return parts.filter(part => normalizeBuildIdentity(part.identity));
}

function scoreDocumentedBuild(build, selectedParts, activeModeId) {
  if (!build?.evidence_eligible) return null;
  const config = build.evidence_config || {};
  const components = config.components || {};

  let comparedWeight = 0;
  let matchedWeight = 0;
  let comparedCount = 0;
  let matchedCount = 0;
  let exactComparedCount = 0;
  let exactMatchedCount = 0;
  const matchedLabels = [];
  const mismatchedLabels = [];

  for (const part of selectedParts) {
    let comparable = false;
    let matched = false;

    if (part.match === 'mobo_type') {
      const buildType = normalizeEvidenceMoboType(config.mobo_form_factor);
      comparable = !!buildType;
      matched = comparable && buildType === normalizeEvidenceMoboType(part.identity);
    } else if (part.match === 'psu_type') {
      const buildPart = components.psu;
      const buildType = resolveBuildPsuFormFactor(buildPart);
      comparable = !!buildType;
      matched = comparable && buildType === normalizeEvidencePsuType(part.identity);
    } else {
      const buildPart = components[part.key];
      comparable = !!buildPart?.name && String(buildPart.name).toUpperCase() !== 'N/A' && !buildPart.is_custom;
      matched = comparable && buildPartMatches(part.identity, buildPart, part.catalogId || '');
    }

    // Unknown/legacy type data is omitted from the denominator rather than being
    // treated as a mismatch. Evidence should only compare facts we can resolve.
    if (!comparable) continue;
    comparedWeight += part.weight;
    comparedCount += 1;
    if (part.match === 'exact') exactComparedCount += 1;
    if (matched) {
      matchedWeight += part.weight;
      matchedCount += 1;
      if (part.match === 'exact') exactMatchedCount += 1;
      matchedLabels.push(part.label);
    } else {
      mismatchedLabels.push(part.label);
    }
  }

  const normalizedMode = String(activeModeId || 'default').trim().toLowerCase();
  const buildMode = String(config.case_mode || '').trim().toLowerCase();
  let modeMatch = null;
  if (normalizedMode && normalizedMode !== 'default' && buildMode) {
    comparedWeight += 2;
    comparedCount += 1;
    modeMatch = normalizedMode === buildMode;
    if (modeMatch) {
      matchedWeight += 2;
      matchedCount += 1;
      matchedLabels.push('Case mode');
    } else {
      mismatchedLabels.push('Case mode');
    }
  }

  if (comparedWeight <= 0) {
    return {
      build,
      similarity: 0,
      comparedCount: 0,
      matchedCount: 0,
      exactComparedCount: 0,
      exactMatchedCount: 0,
      matchedLabels: [],
      mismatchedLabels: [],
      modeMatch,
    };
  }

  return {
    build,
    similarity: matchedWeight / comparedWeight,
    comparedCount,
    matchedCount,
    exactComparedCount,
    exactMatchedCount,
    matchedLabels,
    mismatchedLabels,
    modeMatch,
  };
}

function evidenceBand(best) {
  if (!best || best.comparedCount === 0 || best.matchedCount === 0) return 'CASE_ONLY';

  // Broad motherboard/PSU form-factor matches are useful supporting evidence, but
  // should not by themselves elevate a build to MODERATE/STRONG/EXACT. Once an
  // exact catalog PSU is selected it counts as exact hardware evidence alongside
  // GPU and cooler/AIO identity.
  if (best.exactMatchedCount <= 0) return 'RELATED';
  if (best.comparedCount >= 3 && best.matchedCount === best.comparedCount && best.similarity >= 0.999) return 'EXACT';
  if (best.comparedCount >= 3 && best.similarity >= 0.72) return 'STRONG';
  if (best.comparedCount >= 2 && best.similarity >= 0.50) return 'MODERATE';
  return 'RELATED';
}

function rememberBuildEvidence(key, value) {
  buildEvidenceCache.set(key, value);
  while (buildEvidenceCache.size > BUILD_EVIDENCE_CACHE_LIMIT) {
    buildEvidenceCache.delete(buildEvidenceCache.keys().next().value);
  }
  return value;
}

export async function getConfirmedBuildEvidence(caseObj, formInputs) {
  if (!caseObj || !formInputs) return null;
  const caseSlug = caseObj.case_slug || caseObj.db_slug || caseObj.slug || slugifyUrlSegment(caseObj.name || '');
  if (!caseSlug) return null;

  const activeModeId = getCaseModeSelection(caseObj, 'default');
  const selectedParts = selectedEvidenceParts(formInputs);
  const identityKey = selectedParts.map(part => `${part.key}:${normalizeBuildIdentity(part.identity)}`).join('|');
  const cacheKey = stableKey(['build-evidence-v3', caseSlug, activeModeId, identityKey, formInputs.cooling || 'air']);
  if (buildEvidenceCache.has(cacheKey)) return buildEvidenceCache.get(cacheKey);

  const promise = (async () => {
    const data = await fetchCaseCommunityBuilds(caseSlug, 1, 50);
    if (!data) return null;

    const builds = Array.isArray(data.builds) ? data.builds : [];
    const eligibleBuilds = builds.filter(build => build?.evidence_eligible);
    const scored = eligibleBuilds
      .map(build => scoreDocumentedBuild(build, selectedParts, activeModeId))
      .filter(Boolean)
      .sort((a, b) => {
        if (b.similarity !== a.similarity) return b.similarity - a.similarity;
        if (b.matchedCount !== a.matchedCount) return b.matchedCount - a.matchedCount;
        return Number(b.build?.build_id || 0) - Number(a.build?.build_id || 0);
      });

    const best = scored[0] || null;
    const band = eligibleBuilds.length ? evidenceBand(best) : 'NONE';
    const similar = scored.filter(item => item.matchedCount > 0);
    const exactCount = scored.filter(item => evidenceBand(item) === 'EXACT').length;
    const strongCount = scored.filter(item => ['EXACT', 'STRONG'].includes(evidenceBand(item))).length;
    const documentaryPhotoCount = eligibleBuilds.reduce((total, build) => {
      const photos = Array.isArray(build?.photos) ? build.photos : [];
      return total + photos.length;
    }, 0);

    return {
      source: 'published-community-builds',
      band,
      totalCaseBuilds: Number(data.summary?.total_builds ?? builds.length) || 0,
      // Evidence is counted per completed build, not per photo. Photos are
      // documentation depth for those builds and are exposed separately.
      evidenceEligibleBuilds: eligibleBuilds.length,
      documentaryPhotoCount,
      selectedComparableParts: selectedParts.length,
      exactCount,
      strongCount,
      similarCount: similar.length,
      bestMatch: best ? {
        buildId: best.build.build_id,
        title: best.build.title || 'Community Build',
        permalink: best.build.permalink || '',
        similarityScore: Math.round(best.similarity * 100),
        comparedCount: best.comparedCount,
        matchedCount: best.matchedCount,
        matchedLabels: best.matchedLabels,
        mismatchedLabels: best.mismatchedLabels,
        caseMode: best.build.evidence_config?.case_mode || '',
        photoCount: Array.isArray(best.build?.photos) ? best.build.photos.length : 0,
      } : null,
      // Real-world positive evidence only. No-match is deliberately not a failure signal.
      isNegativeEvidence: false,
    };
  })().catch(error => {
    buildEvidenceCache.delete(cacheKey);
    debugRuntimeIssue('Confirmed Build Evidence', error);
    return null;
  });

  return rememberBuildEvidence(cacheKey, promise);
}

const CLASSIFIER_CONTRACT_VERSION = 3;
const CLASSIFIER_FEATURE_ORDER = [
  'vol', 'maxGpuL', 'maxCpu', 'maxAio',
  'userGpuL', 'userGpuT', 'userGpuH', 'userCpu',
  'cpuDelta', 'gpuLDelta', 'gpuTDelta', 'minHeadroom',
  'isAtxPsu', 'isMatxMobo', 'isSandwich', 'hasSpine',
  'hlRatio', 'isVerticalReference', 'isVerticalSandwich', 'isRadGpuConflict'
];

function isClassifierMetadataCompatible(metadata) {
  if (!metadata || metadata.contract_version !== CLASSIFIER_CONTRACT_VERSION) return false;
  if (metadata.objective !== 'residual_issue_risk_after_deterministic_pass') return false;
  if (metadata.feature_count !== CLASSIFIER_FEATURE_ORDER.length) return false;
  if (!Array.isArray(metadata.feature_order) || metadata.feature_order.length !== CLASSIFIER_FEATURE_ORDER.length) return false;
  return metadata.feature_order.every((name, idx) => name === CLASSIFIER_FEATURE_ORDER[idx]);
}

function residualRiskLevel(score) {
  const numeric = Number(score);
  if (!Number.isFinite(numeric)) return null;
  const lowMax = Math.round(Number(modelMetadata?.risk_bands?.low_max ?? 0.33) * 100);
  const moderateMax = Math.round(Number(modelMetadata?.risk_bands?.moderate_max ?? 0.66) * 100);
  if (numeric <= lowMax) return 'LOW';
  if (numeric <= moderateMax) return 'MODERATE';
  return 'HIGH';
}

/* --------------------------------------------------------------------------
   RELATIONAL GNN WEB WORKER ORCHESTRATOR — CONTRACT V4
   -------------------------------------------------------------------------- */
const GNN_CONTRACT_VERSION = 4;
const GNN_NODE_ORDER = Object.freeze(['case','mobo','gpu','psu','cooler','aio']);
const GNN_CASE_CONTEXT_ORDER = Object.freeze([
  'layout_id','spine_x_mm','spine_y_mm','flip','volume_l',
  'supports_itx','supports_dtx','supports_matx','supports_atx_mobo',
  'supports_sfx','supports_sfx_l','supports_atx_psu','supports_flex_atx',
  'has_spine','max_gpu_l_mm','max_gpu_h_mm','max_gpu_t_mm','max_cpu_h_mm','max_aio_mm','max_psu_l_mm'
]);
const GNN_ADJACENCY = Object.freeze([
  [0,1,1,1,1,1],
  [1,0,1,1,1,1],
  [1,1,0,1,1,1],
  [1,1,1,0,1,1],
  [1,1,1,1,0,0],
  [1,1,1,1,0,0]
]);
const FORM_FACTOR_IDS = Object.freeze({ itx:1,dtx:2,matx:3,atx:4,sfx:5,sfx_l:6,flex_atx:7,custom:8 });
const GNN_TIMEOUT_MS = 5000;
const GNN_READY_TIMEOUT_MS = 2500;
const GNN_CONSTRAINT_PROPERTY_ORDER = Object.freeze(['maxGPU','maxGPUH','maxGPUT','maxCPU','maxAIO','maxPSU']);

let relationalWorker=null;
let workerReady=false;
let workerReadyWaiters=[];
const pendingPredictions=new Map();
const relationalPredictionCache=new Map();
let predReqId=0;

function resolveWorkerReady(value){
  const waiters=workerReadyWaiters; workerReadyWaiters=[];
  waiters.forEach(({resolve})=>resolve(value));
}
function drainPendingPredictions(reason='Relational worker unavailable'){
  for(const [reqId,pending] of pendingPredictions.entries()){
    clearTimeout(pending.timer); pendingPredictions.delete(reqId);
    if(pending.cacheKey) relationalPredictionCache.delete(pending.cacheKey);
    pending.resolve(null);
  }
  if(reason) debugRuntimeIssue('Relational GNN',new Error(reason));
}
function awaitWorkerReady(timeoutMs=GNN_READY_TIMEOUT_MS){
  if(workerReady&&relationalWorker) return Promise.resolve(true);
  return new Promise(resolve=>{
    const waiter={resolve}; workerReadyWaiters.push(waiter);
    setTimeout(()=>{
      const i=workerReadyWaiters.indexOf(waiter); if(i!==-1) workerReadyWaiters.splice(i,1);
      resolve(workerReady&&!!relationalWorker);
    },timeoutMs);
  });
}
function decodeOrientationClasses(logits){
  if(!Array.isArray(logits)||logits.length<GNN_NODE_ORDER.length*24) return [];
  const out=[];
  for(let n=0;n<GNN_NODE_ORDER.length;n++){
    let best=0,bestVal=-Infinity;
    for(let c=0;c<24;c++){
      const v=Number(logits[n*24+c]);
      if(Number.isFinite(v)&&v>bestVal){bestVal=v;best=c;}
    }
    out.push(best);
  }
  return out;
}

export function initRelationalWorker(workerPath=''){
  if(relationalWorker) return relationalWorker;
  const resolvedWorkerPath = workerPath || ((typeof window !== 'undefined' && window.DB_ASSETS?.worker) ? window.DB_ASSETS.worker : '/database/relational-engine.js');
  try{
    relationalWorker=new Worker(resolvedWorkerPath); workerReady=false;
    relationalWorker.onmessage=(e)=>{
      const {type,reqId,deltas,interactions,constraintEffects,orientationLogits,contract_version:cv,error}=e.data||{};
      if(type==='READY'){
        if(cv!==GNN_CONTRACT_VERSION){
          reportRuntimeIssue('Relational GNN',new Error(`Worker contract mismatch: expected v${GNN_CONTRACT_VERSION}, received v${cv??'unknown'}`),'error');
          workerReady=false; resolveWorkerReady(false); return;
        }
        workerReady=true; resolveWorkerReady(true);
        console.log(`[SFF Relational GNN]: Web worker initialized (contract v${GNN_CONTRACT_VERSION}).`);
        return;
      }
      if(type==='PREDICTION_ERROR'&&(reqId===null||reqId===undefined)){
        workerReady=false;
        reportRuntimeIssue('Relational GNN Init',new Error(error||'GNN worker initialization failed'),'error');
        resolveWorkerReady(false); relationalWorker?.terminate?.(); relationalWorker=null; return;
      }
      const pending=pendingPredictions.get(reqId); if(!pending) return;
      clearTimeout(pending.timer); pendingPredictions.delete(reqId);
      if(type==='PREDICTION_SUCCESS'&&cv===GNN_CONTRACT_VERSION){
        const result={
          contractVersion:GNN_CONTRACT_VERSION,
          nodeOrder:[...GNN_NODE_ORDER],
          constraintPropertyOrder:[...GNN_CONSTRAINT_PROPERTY_ORDER],
          deltas:Array.isArray(deltas)?deltas:[],
          interactions:Array.isArray(interactions)?interactions:[],
          constraintEffects:Array.isArray(constraintEffects)?constraintEffects:[],
          orientationLogits:Array.isArray(orientationLogits)?orientationLogits:[],
          orientationClasses:decodeOrientationClasses(Array.isArray(orientationLogits)?orientationLogits:[])
        };
        if(pending.cacheKey) relationalPredictionCache.set(pending.cacheKey,result);
        pending.resolve(result);
      }else{
        if(pending.cacheKey) relationalPredictionCache.delete(pending.cacheKey);
        if(error) debugRuntimeIssue('Relational GNN Prediction',new Error(error));
        pending.resolve(null);
      }
    };
    relationalWorker.onerror=(event)=>{
      workerReady=false; reportRuntimeIssue('Relational GNN Worker',new Error(event?.message||'Worker execution error'));
      drainPendingPredictions('Worker execution error'); resolveWorkerReady(false);
      relationalWorker?.terminate?.(); relationalWorker=null;
    };
    relationalWorker.onmessageerror=()=>{
      workerReady=false; drainPendingPredictions('Worker message deserialization error'); resolveWorkerReady(false);
      relationalWorker?.terminate?.(); relationalWorker=null;
    };
    relationalWorker.postMessage({type:'INIT',contract_version:GNN_CONTRACT_VERSION});
  }catch(err){
    console.warn('[SFF Relational GNN]: Could not initialize web worker:',err);
    relationalWorker=null; workerReady=false; resolveWorkerReady(false);
  }
  return relationalWorker;
}

function getMoboDescriptor(moboType){
  const type=String(moboType||'itx').toLowerCase().replace('-','_');
  if(type==='atx') return {type:'atx',dims:[305,244],id:FORM_FACTOR_IDS.atx};
  if(type==='matx'||type==='m_atx'||type==='micro_atx') return {type:'matx',dims:[244,244],id:FORM_FACTOR_IDS.matx};
  if(type==='dtx') return {type:'dtx',dims:[203,170],id:FORM_FACTOR_IDS.dtx};
  return {type:'itx',dims:[170,170],id:FORM_FACTOR_IDS.itx};
}
function getPsuDescriptor(psuType){
  const raw=String(psuType||'sfx').toLowerCase().replace(/\s+/g,'').replace('-','_');
  if(raw.includes('flex')) return {type:'flex_atx',dims:[150,81.5,40.5],id:FORM_FACTOR_IDS.flex_atx};
  if(raw.includes('sfx_l')||raw.includes('sfxl')) return {type:'sfx_l',dims:[125,130,63.5],id:FORM_FACTOR_IDS.sfx_l};
  if(raw==='atx'||(raw.includes('atx')&&!raw.includes('sfx'))) return {type:'atx',dims:[150,140,86],id:FORM_FACTOR_IDS.atx};
  return {type:'sfx',dims:[125,100,63.5],id:FORM_FACTOR_IDS.sfx};
}
function normalizeMoboSupport(value){
  const raw=String(value||'').trim().toLowerCase().replace('micro-atx','matx').replace('m-atx','matx').replace('micro_atx','matx').replace('m_atx','matx');
  if(raw==='mini-itx'||raw==='mini_itx') return 'itx';
  return ['itx','dtx','matx','atx'].includes(raw)?raw:null;
}
function normalizePsuSupport(value){
  const raw=String(value||'').trim().toLowerCase().replace(/\s+/g,'').replace(/-/g,'_');
  if(raw==='sfx') return 'sfx';
  if(raw==='sfxl'||raw==='sfx_l') return 'sfx_l';
  if(raw==='atx') return 'atx';
  if(raw==='flex'||raw==='flexatx'||raw==='flex_atx') return 'flex_atx';
  return null;
}
function getCaseSupportContext(caseObj){
  const moboRaw=Array.isArray(caseObj?._moboList)?caseObj._moboList:[];
  const psuRaw=Array.isArray(caseObj?._psuList)?caseObj._psuList:[];
  const moboSet=new Set(moboRaw.map(normalizeMoboSupport).filter(Boolean));
  const psuSet=new Set(psuRaw.map(normalizePsuSupport).filter(Boolean));
  if(!moboSet.size||!psuSet.size) return null;
  return {moboSet,psuSet,flags:[
    moboSet.has('itx')?1:0,moboSet.has('dtx')?1:0,moboSet.has('matx')?1:0,moboSet.has('atx')?1:0,
    psuSet.has('sfx')?1:0,psuSet.has('sfx_l')?1:0,psuSet.has('atx')?1:0,psuSet.has('flex_atx')?1:0
  ]};
}
function getLayoutId(caseObj){
  const layout=String(caseObj?._layout||caseObj?.layout||'reference').toLowerCase();
  const dims=Array.isArray(caseObj?._dims)?caseObj._dims:[];
  const vertical=dims.length>=3&&Number(dims[2])>Number(dims[0]);
  if(layout==='apu'||layout==='apu_only') return 7;
  if(layout==='console') return 2;
  if(layout==='bench') return 3;
  if(layout.includes('sandwich')) return vertical?4:1;
  if(layout.includes('reference')||layout.includes('standard')) return vertical?5:0;
  return 0;
}
function radiatorDimensions(rad,thickness=27.2){
  const r=Math.max(0,Number(rad)||0); if(!r) return [0,0,0];
  const width=[140,280,420].includes(Math.round(r))?140:120;
  return [r+37,width,Number(thickness)||27.2];
}
function makeRelationalNode(dims,pos,orientationClass,{socket=0,ffId=FORM_FACTOR_IDS.custom,spine=0,layoutId=0,flip=0,present=1}={}){
  const d=[...dims].map(v=>Number(v)||0), p=[...pos].map(v=>Number(v)||0);
  const rot=orientationMatrixForClass(orientationClass);
  return [d[0],d[1],d[2],p[0],p[1],p[2],...rot,Number(socket)||0,Number(ffId)||0,Number(spine)||0,Number(layoutId)||0,flip?1:0,present?1:0];
}
function applyRelationalNodeOverride(base,override){
  if(!Array.isArray(base)||base.length!==21||!override||typeof override!=='object') return base;
  const out=base.slice();
  const triplet=(v)=>Array.isArray(v)&&v.length>=3&&v.slice(0,3).every(x=>Number.isFinite(Number(x)))?v.slice(0,3).map(Number):null;
  const dims=triplet(override.dims_mm||override.dimensions_mm), pos=triplet(override.position_mm||override.position);
  const matrix=Array.isArray(override.orientation_matrix)&&override.orientation_matrix.length>=9&&override.orientation_matrix.slice(0,9).every(x=>Number.isFinite(Number(x)))?override.orientation_matrix.slice(0,9).map(Number):null;
  if(dims) out.splice(0,3,...dims); if(pos) out.splice(3,3,...pos);
  if(matrix) out.splice(6,9,...matrix);
  else if(Number.isFinite(Number(override.orientation_class))) out.splice(6,9,...orientationMatrixForClass(Number(override.orientation_class)));
  if(Number.isFinite(Number(override.socket_mm??override.socket))) out[15]=Number(override.socket_mm??override.socket);
  if(Number.isFinite(Number(override.form_factor_id))) out[16]=Number(override.form_factor_id);
  if(Number.isFinite(Number(override.spine_x_mm??override.spine))) out[17]=Number(override.spine_x_mm??override.spine);
  if(Number.isFinite(Number(override.layout_id))) out[18]=Number(override.layout_id);
  if(override.flip!==undefined) out[19]=override.flip?1:0;
  if(override.present!==undefined) out[20]=override.present?1:0;
  return out;
}

export async function predictRelationalClearances(caseObj,formInputs,spineStep=null,precomputedEval=null){
  if(!caseObj||!formInputs) return null;
  const activeModeId=getCaseModeSelection(caseObj,'default');
  const engineEval=precomputedEval||checkCompatibility(caseObj,formInputs,activeModeId);
  if(Array.isArray(engineEval.unknowns)&&engineEval.unknowns.length>0) return null;

  // V4 is independent from the scalar Edge classifier. It runs only for the active
  // case/report path, so clear deterministic builds are still eligible for relational inference.
  if(!relationalWorker) initRelationalWorker();
  if(!(await awaitWorkerReady())) return null;

  const support=getCaseSupportContext(caseObj);
  const volume=Number(caseObj._volume??caseObj.volume);
  const caseDims=Array.isArray(caseObj._dims)&&caseObj._dims.length>=3?caseObj._dims.slice(0,3).map(Number):null;
  if(!support||!caseDims||caseDims.some(v=>!Number.isFinite(v)||v<=0)||!Number.isFinite(volume)||volume<=0) return null;

  const modeSpecs=getEffectiveSpecs(caseObj,activeModeId);
  const maxGpuL=Number(modeSpecs.maxGPU||0), maxGpuH=Number(modeSpecs.maxGPUH||0), maxGpuT=Number(modeSpecs.maxGPUT||0);
  const maxCpuH=Number(modeSpecs.maxCPU||0), maxAio=Number(modeSpecs.maxAIO||0), maxPsu=Number(modeSpecs.maxPSU||0);
  const isApu=!!caseObj._isAPU;
  const gpuL=isApu?0:Number(formInputs.gpuL||304), gpuT=isApu?0:Number(formInputs.gpuT||40), gpuH=isApu?0:Number(formInputs.gpuH||137);
  const cpuH=Number(formInputs.cpuH||47);
  const mobo=getMoboDescriptor(formInputs.mobo);
  const psuType=normalizePsuSupport(formInputs.psu); if(!psuType) return null;
  const psu=getPsuDescriptor(psuType);
  const layoutId=getLayoutId(caseObj), layoutStr=String(caseObj._layout||caseObj.layout||'reference');
  const spineOffset=Number(caseObj.spineOffset||0);
  const maxMobo=support.moboSet.has('atx')?getMoboDescriptor('atx'):support.moboSet.has('matx')?getMoboDescriptor('matx'):support.moboSet.has('dtx')?getMoboDescriptor('dtx'):getMoboDescriptor('itx');
  const hasSpine=caseObj.hasSpine?1:0;

  const coolingRaw=String(formInputs.cooling||'air').toLowerCase();
  const liquid=coolingRaw.includes('liquid')||coolingRaw.includes('aio')||coolingRaw.includes('water');
  const rad=liquid?Number(formInputs.rad||240):0;
  const aioExact=getExactSelectedComponentSpecs('aio')||{};
  const cpuExact=getExactSelectedComponentSpecs('cpu')||{};
  const aioDims=liquid?[
    Number(aioExact.actual_length_mm)||radiatorDimensions(rad)[0],
    Number(aioExact.actual_width_mm)||radiatorDimensions(rad)[1],
    Number(aioExact.actual_thickness_mm)||27.2
  ]:[0,0,0];
  const coolerDims=!liquid?[
    Number(cpuExact.length_mm)||120,
    Number(cpuExact.width_mm)||120,
    cpuH
  ]:[0,0,0];

  const prior=getRelationalLayoutSpatialPrior(layoutStr,caseDims,{maxGPU:maxGpuL,maxGPUH:maxGpuH,maxGPUT:maxGpuT,maxCPU:maxCpuH});
  const ori=(node)=>getRelationalOrientationClass(layoutStr,node);
  const nodeOverrides=formInputs.relationalNodeOverrides||formInputs.node_overrides||{};
  const nodes=[
    applyRelationalNodeOverride(makeRelationalNode(caseDims,prior.case,ori('case'),{ffId:maxMobo.id,spine:spineOffset,layoutId,present:1}),nodeOverrides.case),
    applyRelationalNodeOverride(makeRelationalNode([mobo.dims[0],mobo.dims[1],45],prior.mobo,ori('mobo'),{socket:6.5,ffId:mobo.id,spine:spineOffset,layoutId,present:1}),nodeOverrides.mobo),
    applyRelationalNodeOverride(makeRelationalNode([gpuL,gpuT,gpuH],prior.gpu,ori('gpu'),{spine:spineOffset,layoutId,present:isApu?0:1}),nodeOverrides.gpu),
    applyRelationalNodeOverride(makeRelationalNode(psu.dims,prior.psu,ori('psu'),{ffId:psu.id,layoutId,present:1}),nodeOverrides.psu),
    applyRelationalNodeOverride(makeRelationalNode(coolerDims,prior.cooler,ori('cooler'),{layoutId,present:liquid?0:1}),nodeOverrides.cooler),
    applyRelationalNodeOverride(makeRelationalNode(aioDims,prior.aio,0,{layoutId,present:liquid?1:0}),nodeOverrides.aio)
  ];
  // Current 3D engine has no authoritative radiator orientation prior yet.
  // A zero matrix explicitly means unknown rather than falsely asserting identity.
  if (!nodeOverrides.aio?.orientation_matrix && !Number.isFinite(Number(nodeOverrides.aio?.orientation_class))) {
    nodes[5].splice(6, 9, ...Array(9).fill(0));
  }

  const caseContext=[layoutId,spineOffset,0,0,volume,...support.flags,hasSpine,maxGpuL,maxGpuH,maxGpuT,maxCpuH,maxAio,maxPsu];
  if(caseContext.length!==GNN_CASE_CONTEXT_ORDER.length) return null;

  const cacheKey=stableKey([
    GNN_CONTRACT_VERSION,getCaseStateKey(caseObj),activeModeId,
    ...caseContext,gpuL,gpuT,gpuH,cpuH,mobo.type,psu.type,liquid?1:0,rad,
    JSON.stringify(nodeOverrides)
  ]);
  if(relationalPredictionCache.has(cacheKey)) return relationalPredictionCache.get(cacheKey);

  const predictionPromise=new Promise(resolve=>{
    const reqId=++predReqId;
    const timer=setTimeout(()=>{
      const pending=pendingPredictions.get(reqId); if(!pending) return;
      pendingPredictions.delete(reqId); relationalPredictionCache.delete(cacheKey); pending.resolve(null);
      debugRuntimeIssue('Relational GNN',new Error(`Prediction ${reqId} timed out after ${GNN_TIMEOUT_MS}ms`));
    },GNN_TIMEOUT_MS);
    pendingPredictions.set(reqId,{resolve,timer,cacheKey});
    relationalWorker.postMessage({type:'PREDICT',reqId,contract_version:GNN_CONTRACT_VERSION,payload:{
      contract_version:GNN_CONTRACT_VERSION,node_order:GNN_NODE_ORDER,case_context_order:GNN_CASE_CONTEXT_ORDER,
      component_specs:nodes,categorical_ids:[0,1,2,3,4,5],adjacency_matrix:GNN_ADJACENCY,case_context:caseContext
    }});
  });
  relationalPredictionCache.set(cacheKey,predictionPromise);
  const result=await predictionPromise;
  if(result===null) relationalPredictionCache.delete(cacheKey); else relationalPredictionCache.set(cacheKey,result);
  while(relationalPredictionCache.size>256) relationalPredictionCache.delete(relationalPredictionCache.keys().next().value);
  return result;
}

function normalize(val, key, fallbackMin = 0.0, fallbackMax = 1.0) {
  if (val === null || val === undefined || isNaN(val)) return 0.0;
  
  let min = fallbackMin;
  let max = fallbackMax;

  if (modelMetadata && modelMetadata.bounds && modelMetadata.bounds[key]) {
    min = modelMetadata.bounds[key].min ?? fallbackMin;
    max = modelMetadata.bounds[key].max ?? fallbackMax;
  }

  if (max === min) return 0.0;
  return Math.max(0.0, Math.min(1.0, (parseFloat(val) - min) / (max - min)));
}

function applyWebGpuAdapterPolyfill() {
  if (typeof window !== 'undefined' && typeof GPUAdapter !== 'undefined' && !GPUAdapter.prototype.requestAdapterInfo) {
    GPUAdapter.prototype.requestAdapterInfo = async function () {
      return this.info || {};
    };
  }
}

function loadOnnxCdnScript() {
  if (window.ort) return Promise.resolve(window.ort);
  if (onnxRuntimeLoadPromise) return onnxRuntimeLoadPromise;

  onnxRuntimeLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-sff-onnx-runtime="classifier"]');
    const script = existing || document.createElement('script');
    if (!existing) {
      script.dataset.sffOnnxRuntime = 'classifier';
      script.src = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.19.2/dist/ort.webgpu.min.js';
      document.head.appendChild(script);
    }
    script.addEventListener('load', () => resolve(window.ort), { once: true });
    script.addEventListener('error', () => reject(new Error('Failed to load ONNX Runtime WebGPU CDN script.')), { once: true });
  }).catch(error => {
    document.querySelector('script[data-sff-onnx-runtime="classifier"]')?.remove();
    onnxRuntimeLoadPromise = null;
    throw error;
  });

  return onnxRuntimeLoadPromise;
}

async function checkWebGpuAvailability() {
  if (typeof navigator === 'undefined' || !navigator.gpu) return false;
  try {
    applyWebGpuAdapterPolyfill();
    const adapter = await navigator.gpu.requestAdapter();
    return !!adapter;
  } catch (e) {
    return false;
  }
}

async function loadModelMetadata(metaPath = '/models/model_metadata.json') {
  if (modelMetadata) return modelMetadata;
  if (modelMetadataLoadPromise) return modelMetadataLoadPromise;

  // A missing/stale artifact should not cause every eligible interaction to hit
  // model_metadata.json again. Allow an occasional retry for transient deploy/network issues.
  const now = Date.now();
  if (modelMetadataLastAttempt && (now - modelMetadataLastAttempt) < 60_000) return null;
  modelMetadataLastAttempt = now;

  modelMetadataLoadPromise = (async () => {
    try {
      const res = await fetch(metaPath, { cache: 'no-cache' });
      if (!res.ok) return null;
      const candidateMetadata = await res.json();
      if (isClassifierMetadataCompatible(candidateMetadata)) {
        modelMetadata = candidateMetadata;
        console.log('[SFF Edge-AI]: Loaded residual-risk classifier contract v3 from model_metadata.json.');
      } else {
        modelMetadata = null;
        console.warn('[SFF Edge-AI]: Incompatible/stale classifier metadata; residual-risk model disabled until a v3 artifact is deployed.');
      }
    } catch (err) {
      console.warn('[SFF Edge-AI]: Using fallback dynamic bounds:', err);
    }
    return modelMetadata;
  })();

  try {
    return await modelMetadataLoadPromise;
  } finally {
    modelMetadataLoadPromise = null;
  }
}

export async function initMlInstance(modelPath = '/models/sff_fit_classifier.onnx', metaPath = '/models/model_metadata.json') {
  if (ortSession) return ortSession;
  if (classifierInitPromise) return classifierInitPromise;

  const startedAt = runtimeNow();
  classifierInitPromise = (async () => {
    try {
      const metadata = await loadModelMetadata(metaPath);
      if (!metadata) {
        console.warn('[SFF Edge-AI]: Residual-risk inference disabled because no compatible v3 metadata is available.');
        ortSession = null;
        return null;
      }
      applyWebGpuAdapterPolyfill();
      const ort = await loadOnnxCdnScript();

      const isCrossOriginIsolated = typeof self !== 'undefined' && self.crossOriginIsolated;
      ort.env.wasm.numThreads = isCrossOriginIsolated
        ? Math.max(1, Math.floor(navigator.hardwareConcurrency / 2) || 2)
        : 1;
      ort.env.wasm.simd = true;

      const isWebGpuReady = await checkWebGpuAvailability();
      const executionProviders = isWebGpuReady ? ['webgpu', 'wasm'] : ['wasm'];

      ortSession = await ort.InferenceSession.create(modelPath, {
        executionProviders,
        graphOptimizationLevel: 'all'
      });

      console.log(`[SFF Edge-AI]: ONNX session online (${isWebGpuReady ? 'webgpu' : 'wasm'}).`);
      return ortSession;
    } catch (err) {
      console.warn('[SFF Edge-AI]: Falling back to pure Wasm CPU execution provider:', err);
      try {
        const ort = window.ort;
        if (ort) {
          ortSession = await ort.InferenceSession.create(modelPath, {
            executionProviders: ['wasm'],
            graphOptimizationLevel: 'all'
          });
          console.log('[SFF Edge-AI]: ONNX session online (wasm fallback).');
        }
      } catch (fallbackErr) {
        console.error('[SFF Edge-AI]: Critical failure loading ONNX session:', fallbackErr);
        ortSession = null;
      }
      return ortSession;
    } finally {
      debugPerformanceMetric('edge-ai.classifier-init', startedAt, { available: !!ortSession });
    }
  })();

  try {
    return await classifierInitPromise;
  } finally {
    classifierInitPromise = null;
  }
}

export function shouldRunEdgeAiInference(caseObj, formInputs, engineEval) {
  if (!caseObj || !formInputs || !engineEval?.fits) return false;
  if (formInputs.forceEdgeAi === true || formInputs.force_edge_ai === true) return true;
  if (Array.isArray(engineEval.unknowns) && engineEval.unknowns.length > 0) return false;
  if ((engineEval.warns?.length || 0) > 0 || (engineEval.advisories?.length || 0) > 0) return true;

  const specs = engineEval.activeSpecs || {};
  const known = specs.known || {};
  const margins = [];
  const gpuL = Number(formInputs.gpuL || 0);
  const gpuT = Number(formInputs.gpuT || 0);
  const gpuH = Number(formInputs.gpuH || 0);
  const cpuH = Number(formInputs.cpuH || 0);
  const rad = Number(formInputs.rad || 0);

  if (!caseObj._isAPU) {
    if (known.maxGPU && Number(specs.maxGPU) > 0 && gpuL > 0) margins.push((Number(specs.maxGPU) - gpuL) / 20);
    if (known.maxGPUT && Number(specs.maxGPUT) > 0 && gpuT > 0) margins.push((Number(specs.maxGPUT) - gpuT) / 8);
    if (known.maxGPUH && Number(specs.maxGPUH) > 0 && gpuH > 0) margins.push((Number(specs.maxGPUH) - gpuH) / 10);
  }
  if (String(formInputs.cooling || '').toLowerCase() === 'air') {
    if (known.maxCPU && Number(specs.maxCPU) > 0 && cpuH > 0) margins.push((Number(specs.maxCPU) - cpuH) / 10);
  } else if (known.maxAIO && Number(specs.maxAIO) > 0 && rad > 0) {
    margins.push((Number(specs.maxAIO) - rad) / 40);
  }

  if (margins.length && Math.min(...margins) <= 1) return true;

  const volume = Number(caseObj._volume || caseObj.volume || 0);
  const psu = String(formInputs.psu || '').toUpperCase();
  if (caseObj.hasSpine) return true;
  if (volume > 0 && volume < 12 && (psu === 'ATX' || psu === 'SFX-L')) return true;
  return false;
}

function deterministicClassifierResult(engineEval, difficultyScore, cableRiskDetected, extra = {}) {
  return {
    // The deterministic engine remains the only FIT / NO-FIT authority.
    fits: !!engineEval?.fits,
    probability: null,
    fitProbability: null,
    confidenceScore: null,
    residualRiskProbability: null,
    residualRiskScore: null,
    residualRiskLevel: null,
    difficultyRating: difficultyScore,
    cableRiskDetected,
    source: 'deterministic',
    modelAvailable: false,
    ...extra
  };
}

function trimClassifierCache() {
  while (classifierPredictionCache.size > CLASSIFIER_CACHE_LIMIT) {
    const oldestKey = classifierPredictionCache.keys().next().value;
    classifierPredictionCache.delete(oldestKey);
  }
}

function prepareClassifierContext(caseObj, formInputs, precomputedEval = null) {
  if (!caseObj || !formInputs) return { immediate: null };

  const activeModeId = getCaseModeSelection(caseObj, 'default');
  const engineEval = precomputedEval || checkCompatibility(caseObj, formInputs, activeModeId);
  const userCpu = Number(formInputs.cpuH || 47);
  const userGpuL = Number(formInputs.gpuL || 304);
  const userGpuT = Number(formInputs.gpuT || 40);
  const userGpuH = Number(formInputs.gpuH || 137);
  const userRad = Number(formInputs.rad || 240);
  const isLiquid = String(formInputs.cooling || '').toLowerCase() === 'liquid';

  const effSpecs = engineEval.activeSpecs || getEffectiveSpecs(caseObj, activeModeId);
  const maxCpu = Number(effSpecs.maxCPU || 0);
  const maxGpuL = Number(effSpecs.maxGPU || 0);
  const maxGpuT = Number(effSpecs.maxGPUT || 0);
  const maxGpuH = Number(effSpecs.maxGPUH || 0);
  const maxAio = Number(effSpecs.maxAIO || 0);

  const vol = Number(caseObj._volume || 0);
  const gpuLDelta = maxGpuL - userGpuL;
  const gpuHDelta = maxGpuH - userGpuH;
  const cpuDelta = maxCpu - userCpu;
  const psuType = formInputs.psu ? String(formInputs.psu).toUpperCase() : '';

  let difficultyScore = 1;
  if (vol < 10.0) difficultyScore += 2;
  else if (vol < 14.0) difficultyScore += 1;
  if (gpuLDelta < 10.0) difficultyScore += 1;
  if (cpuDelta < 5.0) difficultyScore += 1;
  if ((psuType.includes('SFX-L') || psuType.includes('ATX')) && vol < 14.0) difficultyScore += 1;
  difficultyScore = Math.max(1, Math.min(5, difficultyScore));

  const cableRiskDetected = (gpuHDelta > 0 && gpuHDelta < 15.0) || (psuType.includes('SFX-L') && vol < 11.0) || (psuType.includes('ATX') && vol < 16.0);

  if (!engineEval.fits || (engineEval.fails && engineEval.fails.length > 0)) {
    return { immediate: deterministicClassifierResult(engineEval, difficultyScore, cableRiskDetected, {
      fits: false,
      overrideReason: engineEval.fails ? engineEval.fails.join(' | ') : 'HARD_PHYSICS_COLLISION'
    }) };
  }

  if (Array.isArray(engineEval.unknowns) && engineEval.unknowns.length > 0) {
    return { immediate: deterministicClassifierResult(engineEval, difficultyScore, cableRiskDetected, {
      overrideReason: engineEval.unknowns.join(' | '),
      hasUnknownConstraints: true
    }) };
  }

  const cDims = Array.isArray(caseObj._dims) ? caseObj._dims : [];
  const hasModelGeometry = Number(caseObj._volume) > 0 && Number(cDims[0]) > 0 && Number(cDims[2]) > 0;
  if (!hasModelGeometry) {
    return { immediate: deterministicClassifierResult(engineEval, difficultyScore, cableRiskDetected, {
      overrideReason: 'MODEL_GEOMETRY_UNKNOWN'
    }) };
  }

  if (!shouldRunEdgeAiInference(caseObj, formInputs, engineEval)) {
    return { immediate: deterministicClassifierResult(engineEval, difficultyScore, cableRiskDetected, {
      overrideReason: 'EDGE_AI_GATED_CLEARANCE',
      inferenceSkipped: true
    }) };
  }

  const ort = window.ort;
  if (!ortSession || !ort) {
    return { immediate: deterministicClassifierResult(engineEval, difficultyScore, cableRiskDetected) };
  }

  const isAtxPsu = (psuType === 'ATX' || psuType.includes('ATX')) && !psuType.includes('SFX') && !psuType.includes('FLEX') ? 1.0 : 0.0;
  const moboType = formInputs.mobo ? String(formInputs.mobo).toUpperCase() : '';
  const isMatxMobo = (moboType === 'MATX' || moboType === 'M-ATX' || moboType === 'MICRO-ATX') ? 1.0 : 0.0;
  const layoutType = caseObj._layout || caseObj.layout || '';
  const isSandwich = String(layoutType).toLowerCase().includes('sandwich') ? 1.0 : 0.0;

  const gpuTDelta = maxGpuT - userGpuT;
  const gpuHDeltaForModel = maxGpuH - userGpuH;
  const relevantHeadroom = [gpuLDelta, gpuTDelta, gpuHDeltaForModel];
  if (!isLiquid) relevantHeadroom.push(cpuDelta);
  const minHeadroom = Math.min(...relevantHeadroom);

  const cLen = Number(cDims[0]);
  const cHgt = Number(cDims[2]);
  const hlRatio = cHgt / Math.max(1, cLen);
  const isVerticalReference = (cHgt > cLen && !isSandwich) ? 1.0 : 0.0;
  const isVerticalSandwich = (cHgt > cLen && isSandwich) ? 1.0 : 0.0;
  const isRadGpuConflict = (isLiquid && userRad >= 280 && userGpuT > 22.0 && isSandwich) ? 1.0 : 0.0;

  const inputData = new Float32Array([
    normalize(vol, 'vol', 3, 35),
    normalize(maxGpuL, 'maxGpuL', 150, 400),
    normalize(maxCpu, 'maxCpu', 30, 180),
    normalize(maxAio, 'maxAio', 0, 360),
    normalize(userGpuL, 'userGpuL', 150, 400),
    normalize(userGpuT, 'userGpuT', 20, 90),
    normalize(userGpuH, 'userGpuH', 60, 180),
    normalize(userCpu, 'userCpu', 30, 180),
    normalize(cpuDelta, 'cpuDelta', -50, 100),
    normalize(gpuLDelta, 'gpuLDelta', -50, 150),
    normalize(gpuTDelta, 'gpuTDelta', -50, 50),
    normalize(minHeadroom, 'minHeadroom', -50, 100),
    isAtxPsu,
    isMatxMobo,
    isSandwich,
    caseObj.hasSpine ? 1.0 : 0.0,
    normalize(hlRatio, 'hlRatio', 0.2, 3.0),
    isVerticalReference,
    isVerticalSandwich,
    isRadGpuConflict
  ]);

  const cacheKey = stableKey([
    CLASSIFIER_CONTRACT_VERSION, getCaseStateKey(caseObj), activeModeId,
    maxGpuL, maxGpuT, maxGpuH, maxCpu, maxAio,
    userGpuL, userGpuT, userGpuH, userCpu, userRad,
    formInputs.cooling, formInputs.radFan, formInputs.mobo, formInputs.psu,
    caseObj.hasSpine ? 1 : 0, cLen, cHgt
  ]);

  return {
    immediate: null,
    inputData,
    cacheKey,
    engineEval,
    difficultyScore,
    cableRiskDetected
  };
}

function extractResidualRiskProbabilities(results, batchSize) {
  const outputKeys = Object.keys(results || {});
  const probOutput = results?.probabilities || results?.output_probability || results?.[outputKeys[1]] || results?.[outputKeys[0]];
  const probabilities = new Array(batchSize).fill(0.5);

  if (Array.isArray(probOutput)) {
    for (let i = 0; i < batchSize; i++) {
      const row = probOutput[i];
      if (Array.isArray(row)) probabilities[i] = Number(row[1] ?? row[0] ?? 0.5);
      else if (row && typeof row === 'object') probabilities[i] = Number(row[1] ?? row['1'] ?? row[1.0] ?? 0.5);
      else if (Number.isFinite(Number(row))) probabilities[i] = Number(row);
    }
    return probabilities;
  }

  const data = probOutput?.data;
  if (data) {
    if (data.length >= batchSize * 2) {
      for (let i = 0; i < batchSize; i++) probabilities[i] = Number(data[(i * 2) + 1] ?? 0.5);
    } else if (data.length >= batchSize) {
      for (let i = 0; i < batchSize; i++) probabilities[i] = Number(data[i] ?? 0.5);
    }
  }
  return probabilities.map(p => Number.isFinite(p) ? Math.max(0, Math.min(1, p)) : 0.5);
}

async function runClassifierContexts(contexts) {
  if (!contexts.length) return [];
  const ort = window.ort;
  if (!ortSession || !ort) {
    return contexts.map(ctx => deterministicClassifierResult(ctx.engineEval, ctx.difficultyScore, ctx.cableRiskDetected));
  }

  const releaseMutex = inferenceMutex;
  let resolveMutex;
  inferenceMutex = new Promise(r => { resolveMutex = r; });
  let inputTensor = null;
  let results = null;

  try {
    await releaseMutex;
    const featureCount = CLASSIFIER_FEATURE_ORDER.length;
    const flat = new Float32Array(contexts.length * featureCount);
    contexts.forEach((ctx, idx) => flat.set(ctx.inputData, idx * featureCount));
    const inputName = (ortSession.inputNames && ortSession.inputNames.length > 0) ? ortSession.inputNames[0] : 'float_input';
    inputTensor = new ort.Tensor('float32', flat, [contexts.length, featureCount]);
    results = await ortSession.run({ [inputName]: inputTensor });
    const residualRiskProbabilities = extractResidualRiskProbabilities(results, contexts.length);

    return contexts.map((ctx, idx) => {
      const residualRiskProbability = residualRiskProbabilities[idx];
      const residualRiskScore = Math.round(residualRiskProbability * 100);
      return {
        // Never let the model override physical compatibility authority.
        fits: !!ctx.engineEval?.fits,
        probability: null,
        fitProbability: null,
        confidenceScore: null,
        residualRiskProbability,
        residualRiskScore,
        residualRiskLevel: residualRiskLevel(residualRiskScore),
        difficultyRating: ctx.difficultyScore,
        cableRiskDetected: ctx.cableRiskDetected,
        source: 'onnx-residual-risk',
        modelAvailable: true
      };
    });
  } catch (inferenceError) {
    console.warn('[SFF Edge-AI]: Residual-risk inference warning; retaining deterministic compatibility only:', inferenceError);
    return contexts.map(ctx => deterministicClassifierResult(ctx.engineEval, ctx.difficultyScore, ctx.cableRiskDetected, {
      overrideReason: 'MODEL_INFERENCE_ERROR'
    }));
  } finally {
    try { inputTensor?.dispose?.(); } catch (error) { debugRuntimeIssue('Classifier Tensor Dispose', error); }
    if (results) {
      Object.values(results).forEach(tensor => {
        try { tensor?.dispose?.(); } catch (error) { debugRuntimeIssue('Classifier Output Dispose', error); }
      });
    }
    resolveMutex();
  }
}

export async function predictHardwareClearanceBatch(requests = []) {
  if (!Array.isArray(requests) || requests.length === 0) return [];
  const startedAt = runtimeNow();

  // Classifier runtime is loaded only if at least one deterministic-pass request
  // actually reaches the residual-risk gate. Normal browsing never blocks on ONNX.
  const needsClassifier = requests.some(request => {
    const caseObj = request?.caseObj;
    const formInputs = request?.formInputs;
    if (!caseObj || !formInputs) return false;
    const activeModeId = getCaseModeSelection(caseObj, 'default');
    const engineEval = request.precomputedEval || checkCompatibility(caseObj, formInputs, activeModeId);
    if (!engineEval?.fits || engineEval.unknowns?.length) return false;
    const cDims = Array.isArray(caseObj._dims) ? caseObj._dims : [];
    const hasModelGeometry = Number(caseObj._volume) > 0 && Number(cDims[0]) > 0 && Number(cDims[2]) > 0;
    return hasModelGeometry && shouldRunEdgeAiInference(caseObj, formInputs, engineEval);
  });
  if (needsClassifier && !ortSession) await initMlInstance();

  const output = new Array(requests.length).fill(null);
  const pendingContexts = [];
  const pendingIndexes = [];
  const cachedWaits = [];

  for (let i = 0; i < requests.length; i++) {
    const request = requests[i] || {};
    const ctx = prepareClassifierContext(request.caseObj, request.formInputs, request.precomputedEval || null);
    if (ctx.immediate !== null) {
      output[i] = ctx.immediate;
      continue;
    }

    if (classifierPredictionCache.has(ctx.cacheKey)) {
      cachedWaits.push(Promise.resolve(classifierPredictionCache.get(ctx.cacheKey)).then(result => { output[i] = result; }));
      continue;
    }

    pendingContexts.push(ctx);
    pendingIndexes.push(i);
  }

  if (cachedWaits.length) await Promise.all(cachedWaits);

  if (pendingContexts.length) {
    const batchPromise = runClassifierContexts(pendingContexts);
    pendingContexts.forEach((ctx, idx) => {
      classifierPredictionCache.set(ctx.cacheKey, batchPromise.then(results => results[idx]));
    });

    const results = await batchPromise;
    results.forEach((result, idx) => {
      const ctx = pendingContexts[idx];
      const outIdx = pendingIndexes[idx];
      output[outIdx] = result;
      if (result?.modelAvailable) classifierPredictionCache.set(ctx.cacheKey, result);
      else classifierPredictionCache.delete(ctx.cacheKey);
    });
    trimClassifierCache();
  }

  debugPerformanceMetric('edge-ai.classifier-batch', startedAt, { requests: requests.length, inferred: pendingContexts.length });
  return output;
}

export async function predictHardwareClearance(caseObj, formInputs, spineStep = null, precomputedEval = null) {
  const [result] = await predictHardwareClearanceBatch([{ caseObj, formInputs, precomputedEval }]);
  return result ?? null;
}
